import { useEffect, useMemo, useState } from 'react';
import SortableTable from '../components/SortableTable';
import { fetchAuditLogs } from '../services/firestore';

const PAGE_SIZE = 20;
const KNOWN_ACTIONS = [
  'TRIAL_EXTENDED',
  'PLAN_OVERRIDDEN',
  'DISCOUNT_APPLIED',
  'NOTES_UPDATED',
  'PROMO_CREATED',
  'PROMO_DEACTIVATED',
  'MAINTENANCE_MODE_TOGGLED',
  'FEATURE_FLAG_TOGGLED',
  'BROADCAST_SMS_QUEUED',
  'PLATFORM_WEBHOOK_UPDATED',
  'PLATFORM_WEBHOOK_SECRET_REGENERATED',
  'PLATFORM_WEBHOOK_TEST_SENT',
  'ADMIN_ADDED',
  'ADMIN_REMOVED',
];

function toDateTime(value) {
  if (!value?.toDate) return '-';
  return value.toDate().toLocaleString();
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [action, setAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const logs = await fetchAuditLogs({ action, startDate, endDate });
      setRows(logs);
      setPage(1);
    } catch (err) {
      setError(err?.message || 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [page, rows]);
  const columns = useMemo(() => [
    {
      key: 'timestamp',
      header: 'Timestamp',
      accessor: 'timestamp',
      render: (row) => (
        <button type="button" className="wb-link-btn" onClick={() => setExpandedId((prev) => (prev === row.id ? '' : row.id))}>
          {toDateTime(row.timestamp)}
        </button>
      ),
    },
    { key: 'admin', header: 'Admin', accessor: 'performedBy', render: (row) => row.performedBy || '-' },
    { key: 'action', header: 'Action', accessor: 'action', render: (row) => row.action || '-' },
    { key: 'targetBusiness', header: 'Target Business', accessor: 'targetBusinessId', render: (row) => row.targetBusinessId || '-' },
  ], []);

  return (
    <div className="wb-card">
      <h3 className="wb-section-title">Audit Log</h3>
      <p className="wb-muted">Immutable record. No edit or delete operations are available here.</p>
      <div className="wb-row" style={{ marginBottom: 12 }}>
        <select value={action} onChange={(event) => setAction(event.target.value)}>
          <option value="">All actions</option>
          {KNOWN_ACTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        <button type="button" onClick={load}>Apply</button>
      </div>

      {loading ? <div className="wb-skeleton wb-skeleton--table" /> : null}
      {error ? <p className="wb-error-text">{error}</p> : null}
      {!loading && rows.length === 0 ? <p className="wb-muted">No audit entries found for the current filters.</p> : null}

      {!loading && rows.length > 0 ? (
        <>
          <SortableTable
            columns={columns}
            rows={pageRows}
            emptyText="No audit entries found for the current filters."
            defaultSort={{ key: 'timestamp', dir: 'desc' }}
            renderExpanded={(row, colSpan) => (expandedId === row.id ? (
              <tr>
                <td colSpan={colSpan}>
                  <div className="wb-json-grid">
                    <div>
                      <strong>Before</strong>
                      <pre className="wb-cell-json">{JSON.stringify(row.before, null, 2)}</pre>
                    </div>
                    <div>
                      <strong>After</strong>
                      <pre className="wb-cell-json">{JSON.stringify(row.after, null, 2)}</pre>
                    </div>
                  </div>
                </td>
              </tr>
            ) : null)}
          />

          <div className="wb-pagination">
            <button type="button" disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>Next</button>
          </div>
        </>
      ) : null}
    </div>
  );
}
